import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";

import { neoConversationController } from "../../../AppController";
import { useAppSelector } from "../../../hooks/redux";
import type { NeoConversationDTO } from "../../../hexagon/core/ports/driven/neoConversationRepository";
import {
  COPILOT_CONVERSATION_CREATED_EVENT,
  COPILOT_CONVERSATION_RENAMED_EVENT,
  COPILOT_CONVERSATION_VIEWED_EVENT,
  type CopilotConversationRenamedDetail,
} from "../../copilotEvents";
import { copilotStreams } from "../../pages/Copilot/copilotStreams";

const recentFirst = (
  left: NeoConversationDTO,
  right: NeoConversationDTO
): number => {
  const timestamp = (conversation: NeoConversationDTO) =>
    new Date(
      conversation.last_message_at ??
        conversation.updated_at ??
        conversation.created_at ??
        0
    ).getTime();
  return timestamp(right) - timestamp(left);
};

export const mergeConversationActivity = (
  current: NeoConversationDTO[],
  incoming: NeoConversationDTO[],
  incremental: boolean
): NeoConversationDTO[] => {
  const merged = new Map(
    (incremental ? current : []).map((conversation) => [
      conversation.id,
      conversation,
    ])
  );
  incoming.forEach((conversation) => merged.set(conversation.id, conversation));
  return [...merged.values()].sort(recentFirst);
};

export const useRecentConversations = (enabled: boolean) => {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const asOfRef = useRef<string>();
  const activitySupportedRef = useRef(true);
  const conversationsRef = useRef<NeoConversationDTO[]>([]);
  // Scope the cache to the signed-in user (as useMySpend does) so a logout and
  // login in the same tab never shows the previous account's chat titles.
  const userKey = useAppSelector((state) => {
    const user = state.auth.user;
    return user?.userID
      ? `id:${user.userID}`
      : `email:${user?.email ?? "anonymous"}`;
  });
  const userKeyRef = useRef(userKey);
  if (userKeyRef.current !== userKey) {
    // Drop the previous user's incremental cursor and merge base before the
    // new key's first fetch runs.
    userKeyRef.current = userKey;
    asOfRef.current = undefined;
    activitySupportedRef.current = true;
    conversationsRef.current = [];
  }
  const { data, isLoading, mutate } = useSWR<NeoConversationDTO[]>(
    enabled ? ["ui2026-recent-conversations", userKey] : null,
    async () => {
      const loadLegacyConversations = async () => {
        const legacyResult =
          await neoConversationController.listConversations();
        if (!legacyResult.success || !legacyResult.data) {
          throw new Error(
            legacyResult.error?.message ||
              "Could not load recent conversations."
          );
        }
        const next = legacyResult.data
          .map((conversation) => ({
            ...conversation,
            activity_status: "idle" as const,
          }))
          .sort(recentFirst);
        conversationsRef.current = next;
        return next;
      };

      if (!activitySupportedRef.current) {
        return loadLegacyConversations();
      }

      const since = asOfRef.current;
      const result = await neoConversationController.listConversationActivity({
        ...(since ? { since } : {}),
        limit: 200,
        offset: 0,
        include_archived: false,
        // Workflow runs and ticket workers aren't chats the user opened;
        // the plain list never showed them, so neither does this one.
        include_ticket_workers: false,
        include_workflow_conversations: false,
      });
      if (!result.success || !result.data) {
        if (result.error?.status === 404 || result.error?.status === 422) {
          activitySupportedRef.current = false;
          asOfRef.current = undefined;
          return loadLegacyConversations();
        }
        throw new Error(
          result.error?.message || "Could not load recent conversations."
        );
      }
      if (!Array.isArray(result.data.items)) {
        activitySupportedRef.current = false;
        asOfRef.current = undefined;
        return loadLegacyConversations();
      }
      const next = mergeConversationActivity(
        conversationsRef.current,
        result.data.items,
        Boolean(since)
      );
      // The deployed endpoint has no ``as_of``/``since`` yet: without a
      // cursor, every poll is a full refresh rather than an incremental one.
      asOfRef.current =
        typeof result.data.as_of === "string" ? result.data.as_of : undefined;
      conversationsRef.current = next;
      return next;
    },
    {
      // No keepPreviousData: the key only changes when the user does, and
      // carrying the old key's list over would leak it to the new account.
      revalidateOnFocus: true,
      shouldRetryOnError: false,
      refreshInterval: () => (activitySupportedRef.current ? 5_000 : 60_000),
      dedupingInterval: 1_000,
    }
  );

  useEffect(() => {
    if (!enabled) return;
    const refresh = () => {
      asOfRef.current = undefined;
      void mutate();
    };
    window.addEventListener(COPILOT_CONVERSATION_CREATED_EVENT, refresh);
    window.addEventListener(COPILOT_CONVERSATION_VIEWED_EVENT, refresh);
    return () => {
      window.removeEventListener(COPILOT_CONVERSATION_CREATED_EVENT, refresh);
      window.removeEventListener(COPILOT_CONVERSATION_VIEWED_EVENT, refresh);
    };
  }, [enabled, mutate]);

  useEffect(() => {
    if (enabled) return;
    asOfRef.current = undefined;
    activitySupportedRef.current = true;
    conversationsRef.current = [];
  }, [enabled]);

  const deleteConversation = useCallback(
    async (conversationId: number): Promise<boolean> => {
      if (deletingId !== null) return false;
      const previous = data ?? [];
      setDeletingId(conversationId);
      await mutate(
        (current) => {
          const next = (current ?? []).filter(
            (conversation) => conversation.id !== conversationId
          );
          conversationsRef.current = next;
          return next;
        },
        { revalidate: false }
      );
      const result = await neoConversationController.deleteConversation(
        conversationId
      );
      if (!result.success) {
        conversationsRef.current = previous;
        await mutate(previous, { revalidate: false });
        setDeletingId(null);
        return false;
      }
      // A reply may still be streaming in the background for this chat.
      copilotStreams.get(conversationId)?.abort.abort();
      await mutate();
      setDeletingId(null);
      return true;
    },
    [data, deletingId, mutate]
  );

  const renameConversation = useCallback(
    async (conversationId: number, rawTitle: string): Promise<boolean> => {
      const title = rawTitle.trim();
      if (!title) return false;
      const previous = data ?? [];
      const renamed = previous.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, title }
          : conversation
      );
      conversationsRef.current = renamed;
      await mutate(renamed, { revalidate: false });
      const result = await neoConversationController.updateConversationTitle(
        conversationId,
        title
      );
      if (!result.success) {
        conversationsRef.current = previous;
        await mutate(previous, { revalidate: false });
        return false;
      }
      // Keep the open conversation's header in step with the sidebar.
      window.dispatchEvent(
        new CustomEvent<CopilotConversationRenamedDetail>(
          COPILOT_CONVERSATION_RENAMED_EVENT,
          {
            detail: {
              id: conversationId,
              title: result.data?.title?.trim() || title,
            },
          }
        )
      );
      return true;
    },
    [data, mutate]
  );

  const markConversationViewed = useCallback(
    async (
      conversationId: number,
      throughSequenceNum: number
    ): Promise<boolean> => {
      const result = await neoConversationController.markConversationViewed(
        conversationId,
        throughSequenceNum
      );
      if (!result.success) return false;
      await mutate(
        (current) => {
          const next = (current ?? []).map((conversation) =>
            conversation.id === conversationId
              ? {
                  ...conversation,
                  activity_status: "idle" as const,
                  viewed_through_sequence_num: Math.max(
                    conversation.viewed_through_sequence_num ?? 0,
                    throughSequenceNum
                  ),
                }
              : conversation
          );
          conversationsRef.current = next;
          return next;
        },
        { revalidate: false }
      );
      return true;
    },
    [mutate]
  );

  return {
    conversations: data ?? [],
    deleteConversation,
    deletingId,
    loading: isLoading,
    markConversationViewed,
    renameConversation,
  };
};
